import { Box } from "@chakra-ui/react";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { SelectionAlwaysOnDisplay } from "@lexical/react/LexicalSelectionAlwaysOnDisplay";
import { EditorState, LexicalEditor } from "lexical";
import { useEffect, useState } from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { ContentEditable } from "../components";
import { useSettings } from "../context/SettingsContext";
import { useSharedHistoryContext } from "../context/SharedHistoryContext";
import { EditorProps } from "../Editor";
import ContextMenuPlugin from "../plugins/ContextMenuPlugin";
import { MaxLengthPlugin } from "../plugins/MaxLengthPlugin";
import PasteSanitizePlugin from "../plugins/PasteSanitizePlugin";
import SegmentedMessagePlugin from "../plugins/SegmentedMessagePlugin";
import SmsStatsPlugin from "../plugins/SmsStatsPlugin";
import ToolbarPlugin from "../plugins/ToolbarPlugin";
import VariablesPlugin from "../plugins/VariablesPlugin";

export const LiteVersionEditor = ({
  name,
  id,
  width,
  height,
  maxLength,
  placeholder,
  editorRef,
  onBlur,
  onChange,
}: EditorProps): JSX.Element => {
  const {
    settings: { enableVariables, enableSmsStats, hideToolbar = false },
  } = useSettings();
  const { historyState } = useSharedHistoryContext();

  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);

  const featureFlag = useFlags(["segmented_message_warning"]);

  useEffect(() => {
    if (editorRef) {
      editorRef(activeEditor);
    }
  }, [activeEditor, editorRef]);

  const handleOnBlur = (e: React.FocusEvent<any>) => {
    onBlur?.(editor.getEditorState());
  };

  const handleOnChange = (
    editorState: EditorState,
    editor: LexicalEditor,
    tags: Set<string>
  ) => {
    onChange?.(editorState);
  };

  let smsStats: React.ReactNode = null;
  if (enableSmsStats) {
    if (featureFlag["segmented_message_warning"].enabled) {
      smsStats = <SegmentedMessagePlugin />;
    } else {
      smsStats = <SmsStatsPlugin />;
    }
  }

  return (
    <>
      {!hideToolbar ? (
        <ToolbarPlugin
          editor={editor}
          activeEditor={activeEditor}
          setActiveEditor={setActiveEditor}
          isLite
        />
      ) : null}
      <Box>
        {maxLength && <MaxLengthPlugin maxLength={maxLength} />}
        <PasteSanitizePlugin />
        <SelectionAlwaysOnDisplay />
        <ClearEditorPlugin />
        {enableVariables && <VariablesPlugin />}
        <HistoryPlugin externalHistoryState={historyState} />
        <PlainTextPlugin
          contentEditable={
            <Box className="editor-scroller">
              <Box
                className="editor"
                overflow="auto"
                width={width}
                height={height}
                sx={{ containerType: "inline-size" }}
              >
                <ContentEditable
                  name={name}
                  id={id}
                  placeholder={placeholder || ""}
                  onBlur={handleOnBlur}
                />
              </Box>
            </Box>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        {onChange && (
          <OnChangePlugin
            onChange={handleOnChange}
            ignoreSelectionChange={true}
          />
        )}
        <ContextMenuPlugin />
      </Box>
      {smsStats}
    </>
  );
};
